// This module handles initialization, state management, and event handlers

(function() {
  'use strict';

  // Prevent multiple initializations
  if (window.packageBuilderInitialized) {
    return;
  }
  window.packageBuilderInitialized = true;

  // Application State
  const pbState = {
    filterOptions: {},
    productSelections: {},
    selections: [],
    expandedAccordion: null,
    loading: false,
    quantities: {}  // { [productId]: number } — tracks qty per product, default 1
  };

  // Make state accessible for other modules
  window.PB_STATE = pbState;

  // Helper to get quantity for a product
  window.PB_getQuantity = function(productId) {
    return pbState.quantities[productId] || 1;
  };


  async function pbInit() {
    window.PB_showLoading(true);

    try {
      // Wait for MAP prices to load first
      await window.PB_MAP_PRICES_LOADED;

      // Load filter options from all collections
      for (const collection of window.PB_CONFIG.collections) {
        await window.PB_loadCollectionOptions(collection, pbState);
      }

      window.PB_renderAccordions(pbState, window.PB_CONFIG);
      pbUpdateSavedCartsUI();
    } catch (error) {
      console.error('Initialization error:', error);
      document.getElementById('pbAccordions').innerHTML = `
        <div style="padding: 40px; text-align: center; background: #2d2d2d; border-radius: 8px; margin: 20px 0; border: 1px solid #444;">
          <h3 style="color: #d32f2f; margin-bottom: 10px;">Error Loading Package Builder</h3>
          <p style="color: #999;">Failed to load product collections. Please check:</p>
          <ul style="text-align: left; max-width: 500px; margin: 20px auto; color: #999;">
            <li>Collection handles are correct in settings</li>
            <li>Collections exist and have products</li>
            <li>Browser console for detailed errors</li>
          </ul>
          <button onclick="location.reload()" style="padding: 10px 20px; background: #c9a55a; color: white; border: none; border-radius: 6px; cursor: pointer;">
            Reload Page
          </button>
        </div>
      `;
    } finally {
      window.PB_showLoading(false);
    }
  }


  function pbHandleSizeChange(classId, size) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedSize = size || undefined;

    if (pbState.productSelections[classId].selectedFinishId) {
      pbFetchFilteredProducts(classId);
    }

    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleFinishChange(classId, finishId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedFinishId = parseInt(finishId) || undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleFuelTypeChange(classId, fuelTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedFuelTypeId = parseInt(fuelTypeId) || undefined;

    // Clear size if not valid for fuel type
    if (classId === 'ranges' && fuelTypeId) {
      const options = pbState.filterOptions[classId];
      const selectedFuelType = options?.fuelTypes?.find(f => f.id === parseInt(fuelTypeId));
      const currentSize = pbState.productSelections[classId].selectedSize;

      if (selectedFuelType && currentSize) {
        let validSizes = [];
        if (selectedFuelType.name === 'Dual Fuel') validSizes = ['24 Inch', '30 Inch', '36 Inch', '48 Inch', '60 Inch'];
        else if (selectedFuelType.name === 'All Gas') validSizes = ['24 Inch', '30 Inch', '36 Inch', '48 Inch'];
        else if (selectedFuelType.name === 'Induction') validSizes = ['30 Inch', '36 Inch'];

        if (validSizes.length > 0 && !validSizes.includes(currentSize)) {
          pbState.productSelections[classId].selectedSize = undefined;
        }
      }
    }

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleAccentFinishChange(classId, accentId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedAccentFinishId = parseInt(accentId) || undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleHelixChange(classId, helixId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedHelixId = parseInt(helixId) || undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleHandleTypeChange(classId, handleTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedHandleTypeId = parseInt(handleTypeId) || undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleMountTypeChange(classId, mountTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedMountTypeId = parseInt(mountTypeId) || undefined;

    // Clear size if not valid for mount type
    if (classId === 'hoods' && mountTypeId) {
      const options = pbState.filterOptions[classId];
      const selectedMountType = options?.mountTypes?.find(m => m.id === parseInt(mountTypeId));
      const currentSize = pbState.productSelections[classId].selectedSize;

      if (selectedMountType && currentSize) {
        let validSizes = [];
        if (selectedMountType.name === 'Wall Mount') validSizes = ['24 Inch', '30 Inch', '36 Inch', '48 Inch', '60 Inch'];
        else if (selectedMountType.name === 'Island Mount') validSizes = ['30 Inch', '36 Inch', '48 Inch', '60 Inch'];
        else if (selectedMountType.name === 'Under Cabinet') validSizes = ['30 Inch', '36 Inch', '42 Inch', '48 Inch', '60 Inch'];
        else if (selectedMountType.name === 'Insert') validSizes = ['28 Inch', '34 Inch', '40 Inch', '46 Inch'];

        if (validSizes.length > 0 && !validSizes.includes(currentSize)) {
          pbState.productSelections[classId].selectedSize = undefined;
        }
      }
    }

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleRefrigeratorTypeChange(classId, refrigeratorTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedRefrigeratorTypeId = parseInt(refrigeratorTypeId) || undefined;

    const options = pbState.filterOptions[classId];
    const selectedType = refrigeratorTypeId ?
      options?.refrigeratorTypes?.find(r => r.id === parseInt(refrigeratorTypeId)) : null;

    if (selectedType?.name !== 'Built In Refrigeration') {
      pbState.productSelections[classId].selectedInteriorFinishId = undefined;
      pbState.productSelections[classId].selectedDoorTypeId = undefined;
    }

    // Clear size if not valid
    if ((classId === 'refrigerators' || classId === 'column-refrigerators') && refrigeratorTypeId) {
      const currentSize = pbState.productSelections[classId].selectedSize;
      if (selectedType && currentSize) {
        let validSizes = [];
        if (selectedType.name === 'Counter Depth' || selectedType.name === 'Standard Depth') validSizes = ['36 Inch'];
        else if (selectedType.name === 'Built In Refrigeration') validSizes = ['30 Inch', '36 Inch', '60 Inch', '72 Inch'];
        else if (selectedType.name === 'Column Wine' || selectedType.name === 'Column Freezer') validSizes = ['18 Inch', '24 Inch'];
        else if (selectedType.name === 'Column Refrigeration') validSizes = ['24 Inch', '30 Inch'];

        if (validSizes.length > 0 && !validSizes.includes(currentSize)) {
          pbState.productSelections[classId].selectedSize = undefined;
        }
      }
    }

    pbFetchFilteredProducts(classId);
    window.PB_renderSingleAccordion(classId, pbState);
  }

  function pbHandleColumnRefrigeratorTypeChange(classId, columnRefrigeratorTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedColumnRefrigeratorTypeId = parseInt(columnRefrigeratorTypeId) || undefined;

    const options = pbState.filterOptions[classId];
    const selectedType = columnRefrigeratorTypeId ?
      options?.columnRefrigeratorTypes?.find(r => r.id === parseInt(columnRefrigeratorTypeId)) : null;

    // Clear size if not valid for the selected type
    const currentSize = pbState.productSelections[classId].selectedSize;
    if (selectedType && currentSize) {
      let validSizes = [];
      if (selectedType.name === 'Column Wine' || selectedType.name === 'Column Freezer') validSizes = ['18 Inch', '24 Inch'];
      else if (selectedType.name === 'Column Refrigeration') validSizes = ['24 Inch', '30 Inch'];

      if (validSizes.length > 0 && !validSizes.includes(currentSize)) {
        pbState.productSelections[classId].selectedSize = undefined;
      }
    }

    pbFetchFilteredProducts(classId);
    window.PB_renderSingleAccordion(classId, pbState);
  }

  function pbHandlePanelTypeChange(classId, panelTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedPanelTypeId = parseInt(panelTypeId) || undefined;

    const options = pbState.filterOptions[classId];
    const panelType = panelTypeId ?
      options?.panelTypes?.find(p => p.id === parseInt(panelTypeId)) : null;

    if (panelType && panelType.name === 'Panel Ready') {
      pbState.productSelections[classId].selectedFinishId = undefined;
      pbState.productSelections[classId].selectedAccentFinishId = undefined;
      pbState.productSelections[classId].selectedHelixId = undefined;
    }

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleInteriorFinishChange(classId, interiorFinishId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedInteriorFinishId = parseInt(interiorFinishId) || undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleDoorTypeChange(classId, doorTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedDoorTypeId = parseInt(doorTypeId) || undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleMicrowaveTypeChange(classId, microwaveTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedMicrowaveTypeId = parseInt(microwaveTypeId) || undefined;

    // Clear size if not valid
    if (classId === 'microwaves' && microwaveTypeId) {
      const options = pbState.filterOptions[classId];
      const selectedType = options?.microwaveTypes?.find(t => t.id === parseInt(microwaveTypeId));
      const currentSize = pbState.productSelections[classId].selectedSize;

      if (selectedType && currentSize) {
        let validSizes = [];
        if (selectedType.name === 'Built In Microwave Oven' || selectedType.name === 'Built In Microwave Drawer') validSizes = ['24 Inch', '30 Inch'];
        else if (selectedType.name === 'Over The Range Microwave') validSizes = ['30 Inch'];

        if (validSizes.length > 0 && !validSizes.includes(currentSize)) {
          pbState.productSelections[classId].selectedSize = undefined;
        }
      }
    }

    pbFetchFilteredProducts(classId);
    window.PB_renderSingleAccordion(classId, pbState);
  }

  function pbHandleRangetopTypeChange(classId, rangetopTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedRangetopTypeId = parseInt(rangetopTypeId) || undefined;

    // Clear size if not valid
    if (classId === 'rangetops' && rangetopTypeId) {
      const options = pbState.filterOptions[classId];
      const selectedType = options?.rangetopTypes?.find(t => t.id === parseInt(rangetopTypeId));
      const currentSize = pbState.productSelections[classId].selectedSize;

      if (selectedType && currentSize) {
        let validSizes = [];
        if (selectedType.name === 'Range Top') validSizes = ['30 Inch', '36 Inch', '48 Inch'];
        else if (selectedType.name === 'Drop In') validSizes = ['30 Inch', '36 Inch'];

        if (validSizes.length > 0 && !validSizes.includes(currentSize)) {
          pbState.productSelections[classId].selectedSize = undefined;
        }
      }
    }

    pbFetchFilteredProducts(classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbHandleUnderCounterTypeChange(classId, underCounterTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedUnderCounterTypeId = parseInt(underCounterTypeId) || undefined;

    // Clear size if not valid
    if (classId === 'under-counter-refrigeration' && underCounterTypeId) {
      const options = pbState.filterOptions[classId];
      const selectedType = options?.underCounterTypes?.find(t => t.id === parseInt(underCounterTypeId));
      const currentSize = pbState.productSelections[classId].selectedSize;

      if (selectedType && currentSize) {
        let validSizes = [];
        if (selectedType.name === 'Ice Maker') validSizes = ['15 Inch'];
        else validSizes = ['24 Inch'];

        if (validSizes.length > 0 && !validSizes.includes(currentSize)) {
          pbState.productSelections[classId].selectedSize = undefined;
        }
      }
    }

    pbFetchFilteredProducts(classId);
    window.PB_renderSingleAccordion(classId, pbState);
  }

  function pbHandleNumberOfOvensChange(classId, ovensId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedNumberOfOvensId = parseInt(ovensId) || undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderSingleAccordion(classId, pbState);
  }

  function pbHandlePlumbingTypeChange(classId, plumbingTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedPlumbingTypeId = parseInt(plumbingTypeId) || undefined;
    // Clear sub-type when main type changes
    pbState.productSelections[classId].selectedPlumbingSubTypeId = undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderSingleAccordion(classId, pbState);
  }

  function pbHandlePlumbingSubTypeChange(classId, plumbingSubTypeId) {
    if (!pbState.productSelections[classId]) {
      pbState.productSelections[classId] = {};
    }
    pbState.productSelections[classId].selectedPlumbingSubTypeId = parseInt(plumbingSubTypeId) || undefined;
    // Clear finish selection when product type changes (different product types have different finishes)
    pbState.productSelections[classId].selectedFinishId = undefined;

    pbFetchFilteredProducts(classId);
    window.PB_renderSingleAccordion(classId, pbState);
  }

  function pbFetchFilteredProducts(classId) {
    const options = pbState.filterOptions[classId];
    const selection = pbState.productSelections[classId];

    if (!options || !selection) return;

    // DEBUG: Log filter state for column-refrigerators
    if (classId === 'column-refrigerators') {
      console.log('=== DEBUG: Column Refrigerators Filter ===');
      console.log('Total products in collection:', options.allProducts?.length);
      console.log('Selection state:', JSON.stringify(selection, null, 2));
      console.log('Available columnRefrigeratorTypes:', options.columnRefrigeratorTypes);
      console.log('Available panelTypes:', options.panelTypes);
      console.log('Available sizes:', options.sizes);
      console.log('Available finishes:', options.finishes);
    }

    // Validate required filters
    const requiredFilters = getRequiredFilters(classId, options, selection);

    if (requiredFilters.length > 0) {
      if (classId === 'column-refrigerators') {
        console.log('DEBUG: Missing required filters:', requiredFilters);
      }
      const existingIdx = pbState.selections.findIndex(s => s.productClassId === classId);
      if (existingIdx !== -1) {
        pbState.selections[existingIdx].filteredProducts = [];
      }
      window.PB_renderAccordions(pbState, window.PB_CONFIG);
      return;
    }

    window.PB_showLoading(true);

    // Get selected filter values
    const selectedFinish = options.finishes.find(f => f.id === selection.selectedFinishId);
    const selectedFuelType = selection.selectedFuelTypeId ? options.fuelTypes?.find(f => f.id === selection.selectedFuelTypeId) : null;
    const selectedAccent = selection.selectedAccentFinishId ? options.accentFinishes.find(a => a.id === selection.selectedAccentFinishId) : null;
    const selectedHandleType = selection.selectedHandleTypeId ? options.handleTypes?.find(ht => ht.id === selection.selectedHandleTypeId) : null;
    const selectedMountType = selection.selectedMountTypeId ? options.mountTypes?.find(m => m.id === selection.selectedMountTypeId) : null;
    const selectedRefrigeratorType = selection.selectedRefrigeratorTypeId ? options.refrigeratorTypes?.find(r => r.id === selection.selectedRefrigeratorTypeId) : null;
    const selectedColumnRefrigeratorType = selection.selectedColumnRefrigeratorTypeId ? options.columnRefrigeratorTypes?.find(r => r.id === selection.selectedColumnRefrigeratorTypeId) : null;
    const selectedPanelType = selection.selectedPanelTypeId ? options.panelTypes?.find(n => n.id === selection.selectedPanelTypeId) : null;
    const selectedInteriorFinish = selection.selectedInteriorFinishId ? options.interiorFinishes?.find(i => i.id === selection.selectedInteriorFinishId) : null;
    const selectedDoorType = selection.selectedDoorTypeId ? options.doorTypes?.find(d => d.id === selection.selectedDoorTypeId) : null;
    const selectedMicrowaveType = selection.selectedMicrowaveTypeId ? options.microwaveTypes?.find(m => m.id === selection.selectedMicrowaveTypeId) : null;
    const selectedUnderCounterType = selection.selectedUnderCounterTypeId ? options.underCounterTypes?.find(u => u.id === selection.selectedUnderCounterTypeId) : null;
    const selectedRangetopType = selection.selectedRangetopTypeId ? options.rangetopTypes?.find(r => r.id === selection.selectedRangetopTypeId) : null;
    const selectedNumberOfOvens = selection.selectedNumberOfOvensId ? options.numberOfOvens?.find(o => o.id === selection.selectedNumberOfOvensId) : null;
    const selectedPlumbingType = selection.selectedPlumbingTypeId ? options.plumbingTypes?.find(p => p.id === selection.selectedPlumbingTypeId) : null;

    // Get the plumbing sub-type name from the ID
    let selectedPlumbingSubTypeName = null;
    if (selection.selectedPlumbingSubTypeId && selectedPlumbingType && options.plumbingSubTypes) {
      const subTypes = options.plumbingSubTypes[selectedPlumbingType.name] || [];
      selectedPlumbingSubTypeName = subTypes.find(name =>
        name.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0) === selection.selectedPlumbingSubTypeId
      );
    }

    // DEBUG: Log selected filter values for column-refrigerators
    if (classId === 'column-refrigerators') {
      console.log('DEBUG: Selected filters:');
      console.log('  - Size:', selection.selectedSize);
      console.log('  - Column Type:', selectedColumnRefrigeratorType?.name);
      console.log('  - Panel Type:', selectedPanelType?.name);
      console.log('  - Finish:', selectedFinish?.name);
    }

    // Filter products
    let debugCount = 0;
    const filtered = options.allProducts.filter(product => {
      let match = true;
      let debugReasons = [];

      if (selection.selectedSize) {
        const sizeMatch = product.tags?.some(tag => tag === selection.selectedSize);
        if (!sizeMatch) debugReasons.push(`Size: looking for "${selection.selectedSize}"`);
        match = match && sizeMatch;
      }

      if (selectedFinish) {
        const finishMatch = product.tags?.some(tag => tag === selectedFinish.name);
        if (!finishMatch) debugReasons.push(`Finish: looking for "${selectedFinish.name}"`);
        match = match && finishMatch;
      }

      if (selectedFuelType) {
        const fuelMatch = product.tags?.some(tag => {
          if (selectedFuelType.name === 'All Gas' && tag === 'All Gas') return true;
          if (selectedFuelType.name === 'Dual Fuel' && tag === 'Dual Fuel') return true;
          if (selectedFuelType.name === 'Induction' && tag === 'Induction') return true;
          return false;
        });
        match = match && fuelMatch;
      }

      if (selectedAccent) {
        const accentMatch = product.tags?.some(tag => tag === selectedAccent.name);
        match = match && accentMatch;
      }

      if (selectedHandleType) {
        const handleTypeMatch = product.tags?.some(tag => tag === selectedHandleType.name);
        match = match && handleTypeMatch;
      }

      if (selectedMountType) {
        const mountMatch = product.tags?.some(tag => tag === selectedMountType.name);
        match = match && mountMatch;
      }

      if (selectedRefrigeratorType) {
        const refrigeratorMatch = product.tags?.some(tag => tag === selectedRefrigeratorType.name);
        match = match && refrigeratorMatch;
      }

      if (selectedColumnRefrigeratorType) {
        const columnRefrigeratorMatch = product.tags?.some(tag => tag === selectedColumnRefrigeratorType.name);
        if (!columnRefrigeratorMatch) debugReasons.push(`ColumnType: looking for "${selectedColumnRefrigeratorType.name}"`);
        match = match && columnRefrigeratorMatch;
      }

      if (selectedPanelType) {
        const panelTypeMatch = product.tags?.some(tag => tag === selectedPanelType.name);
        if (!panelTypeMatch) debugReasons.push(`PanelType: looking for "${selectedPanelType.name}"`);
        match = match && panelTypeMatch;
      }

      // DEBUG: Log why products are excluded (first 5 only)
      if (classId === 'column-refrigerators' && !match && debugCount < 5) {
        console.log(`DEBUG: Product excluded - "${product.title}"`);
        console.log('  Product tags:', product.tags);
        console.log('  Failed checks:', debugReasons);
        debugCount++;
      }

      if (selectedInteriorFinish) {
        const interiorFinishMatch = product.tags?.some(tag => tag === selectedInteriorFinish.name);
        match = match && interiorFinishMatch;
      }

      if (selectedDoorType) {
        const doorTypeMatch = product.tags?.some(tag => tag === selectedDoorType.name);
        match = match && doorTypeMatch;
      }

      if (selectedMicrowaveType) {
        const microwaveTypeMatch = product.tags?.some(tag => tag === selectedMicrowaveType.name);
        match = match && microwaveTypeMatch;
      }

      if (selectedUnderCounterType) {
        const underCounterTypeMatch = product.tags?.some(tag => tag === selectedUnderCounterType.name);
        match = match && underCounterTypeMatch;
      }

      if (selectedRangetopType) {
        const rangetopTypeMatch = product.tags?.some(tag => tag === selectedRangetopType.name);
        match = match && rangetopTypeMatch;
      }

      if (selectedNumberOfOvens) {
        const numberOfOvensMatch = product.tags?.some(tag => tag === selectedNumberOfOvens.name);
        match = match && numberOfOvensMatch;
      }

      if (selectedPlumbingType) {
        const plumbingTypeMatch = product.tags?.some(tag => tag === selectedPlumbingType.name);
        match = match && plumbingTypeMatch;
      }

      if (selectedPlumbingSubTypeName) {
        const plumbingSubTypeMatch = product.tags?.some(tag => tag === selectedPlumbingSubTypeName);
        match = match && plumbingSubTypeMatch;
      }

      return match;
    });

    // DEBUG: Log filter results for column-refrigerators
    if (classId === 'column-refrigerators') {
      console.log(`DEBUG: Filter complete - ${filtered.length} products matched out of ${options.allProducts?.length}`);
      if (filtered.length > 0) {
        console.log('DEBUG: Matched products:', filtered.map(p => p.title));
      }
    }

    // Update selections
    const existingIdx = pbState.selections.findIndex(s => s.productClassId === classId);
    const newSelection = {
      productClassId: classId,
      productClassName: options.className,
      size: selection.selectedSize,
      finish: selectedFinish,
      fuelType: selectedFuelType,
      accentFinish: selectedAccent,
      filteredProducts: filtered,
      selectedProducts: existingIdx >= 0 ? pbState.selections[existingIdx].selectedProducts : []
    };

    if (existingIdx >= 0) {
      pbState.selections[existingIdx] = newSelection;
    } else {
      pbState.selections.push(newSelection);
    }

    window.PB_renderAccordions(pbState, window.PB_CONFIG);
    window.PB_showLoading(false);
  }

  function getRequiredFilters(classId, options, selection) {
    const requiredFilters = [];

    if (options.sizes && options.sizes.length > 0 && !selection.selectedSize) {
      requiredFilters.push('Size');
    }

    const panelType = selection.selectedPanelTypeId ?
      options.panelTypes?.find(p => p.id === selection.selectedPanelTypeId) : null;
    const finishRequired = !options.panelTypes ||
      (panelType && (panelType.name === 'Panel Included' || panelType.name === 'Dishwasher Panels'));

    if (options.finishes && options.finishes.length > 0 && finishRequired && !selection.selectedFinishId) {
      requiredFilters.push('Finish');
    }
    if (options.fuelTypes && options.fuelTypes.length > 0 && !selection.selectedFuelTypeId) {
      requiredFilters.push('Fuel Type');
    }
    if (options.mountTypes && options.mountTypes.length > 0 && !selection.selectedMountTypeId) {
      requiredFilters.push('Mount Type');
    }
    if (options.refrigeratorTypes && options.refrigeratorTypes.length > 0 && !selection.selectedRefrigeratorTypeId) {
      requiredFilters.push('Refrigerator Type');
    }
    if (options.columnRefrigeratorTypes && options.columnRefrigeratorTypes.length > 0 && !selection.selectedColumnRefrigeratorTypeId) {
      requiredFilters.push('Column Refrigerator Type');
    }
    if (options.microwaveTypes && options.microwaveTypes.length > 0 && !selection.selectedMicrowaveTypeId) {
      requiredFilters.push('Microwave Type');
    }
    if (options.rangetopTypes && options.rangetopTypes.length > 0 && !selection.selectedRangetopTypeId) {
      requiredFilters.push('Type');
    }
    if (options.underCounterTypes && options.underCounterTypes.length > 0 && !selection.selectedUnderCounterTypeId) {
      requiredFilters.push('Type');
    }
    if (options.numberOfOvens && options.numberOfOvens.length > 0 && !selection.selectedNumberOfOvensId) {
      requiredFilters.push('Number of Ovens');
    }
    if (options.panelTypes && options.panelTypes.length > 0 && !selection.selectedPanelTypeId) {
      requiredFilters.push('Panel Type');
    }
    if (options.plumbingTypes && options.plumbingTypes.length > 0 && !selection.selectedPlumbingTypeId) {
      requiredFilters.push('Plumbing Type');
    }
    if (selection.selectedPlumbingTypeId && !selection.selectedPlumbingSubTypeId) {
      requiredFilters.push('Product Type');
    }

    return requiredFilters;
  }


  function pbToggleProduct(classId, productId) {
    const selectionIdx = pbState.selections.findIndex(s => s.productClassId === classId);
    if (selectionIdx < 0) return;

    const product = pbState.selections[selectionIdx].filteredProducts.find(p => p.id === productId);
    if (!product) return;

    const selectedProducts = pbState.selections[selectionIdx].selectedProducts || [];
    const existingIdx = selectedProducts.findIndex(p => p.id === productId);

    if (existingIdx >= 0) {
      selectedProducts.splice(existingIdx, 1);
      delete pbState.quantities[productId];
    } else {
      // Remove from other categories first
      pbState.selections.forEach((selection, idx) => {
        if (idx !== selectionIdx && selection.selectedProducts) {
          const duplicateIdx = selection.selectedProducts.findIndex(p => p.id === productId);
          if (duplicateIdx >= 0) {
            selection.selectedProducts.splice(duplicateIdx, 1);
            delete pbState.quantities[productId];
          }
        }
      });
      // Stamp selected finish/accent onto product from current filter selections
      const options = pbState.filterOptions[classId];
      const selection = pbState.productSelections[classId];
      if (options && selection) {
        const finish = selection.selectedFinishId ? options.finishes?.find(f => f.id === selection.selectedFinishId) : null;
        const accent = selection.selectedAccentFinishId ? options.accentFinishes?.find(a => a.id === selection.selectedAccentFinishId) : null;
        if (finish) product.selectedFinish = finish.name;
        if (accent) product.selectedAccent = accent.name;
      }
      selectedProducts.push(product);
      pbState.quantities[productId] = 1;
    }

    pbState.selections[selectionIdx].selectedProducts = selectedProducts;

    window.PB_renderAccordions(pbState, window.PB_CONFIG);
    window.PB_updateCart(pbState, window.PB_CONFIG);
  }

  function pbRemoveProduct(classId, productId) {
    const selectionIdx = pbState.selections.findIndex(s => s.productClassId === classId);
    if (selectionIdx < 0) return;

    const selectedProducts = pbState.selections[selectionIdx].selectedProducts || [];
    const existingIdx = selectedProducts.findIndex(p => p.id == productId);

    if (existingIdx >= 0) {
      selectedProducts.splice(existingIdx, 1);
      delete pbState.quantities[productId];
      pbState.selections[selectionIdx].selectedProducts = selectedProducts;
      window.PB_renderAccordions(pbState, window.PB_CONFIG);
      window.PB_updateCart(pbState, window.PB_CONFIG);
    }
  }


  function pbToggleAccordion(classId) {
    pbState.expandedAccordion = pbState.expandedAccordion === classId ? null : classId;
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
  }

  function pbResetClass(classId) {
    // Clean up quantities for products in this category
    const selection = pbState.selections.find(s => s.productClassId === classId);
    if (selection && selection.selectedProducts) {
      selection.selectedProducts.forEach(p => delete pbState.quantities[p.id]);
    }
    delete pbState.productSelections[classId];
    pbState.selections = pbState.selections.filter(s => s.productClassId !== classId);
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
    window.PB_updateCart(pbState, window.PB_CONFIG);
  }

  function pbRestart() {
    pbState.productSelections = {};
    pbState.selections = [];
    pbState.expandedAccordion = null;
    pbState.quantities = {};
    pbState.activeCartId = null;
    pbUpdateSavedCartsUI();
    window.PB_renderAccordions(pbState, window.PB_CONFIG);
    window.PB_updateCart(pbState, window.PB_CONFIG);
  }

  // ========== Saved Carts (localStorage) ==========

  const PB_STORAGE_KEY = window.PB_CUSTOMER_ID ? 'pb_saved_carts_' + window.PB_CUSTOMER_ID : 'pb_saved_carts';
  const PB_MAX_CARTS = 10;

  // Migrate carts from old unscoped key to customer-scoped key
  if (window.PB_CUSTOMER_ID) {
    try {
      const oldCarts = JSON.parse(localStorage.getItem('pb_saved_carts')) || [];
      if (oldCarts.length > 0) {
        const existing = JSON.parse(localStorage.getItem(PB_STORAGE_KEY)) || [];
        const merged = existing.concat(oldCarts).slice(0, PB_MAX_CARTS);
        localStorage.setItem(PB_STORAGE_KEY, JSON.stringify(merged));
        localStorage.removeItem('pb_saved_carts');
      }
    } catch (e) { /* ignore */ }
  }

  function pbGetSavedCarts() {
    try {
      return JSON.parse(localStorage.getItem(PB_STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function pbSaveSavedCarts(carts) {
    try {
      localStorage.setItem(PB_STORAGE_KEY, JSON.stringify(carts));
    } catch (e) {
      alert('Could not save cart. Browser storage may be full.');
    }
  }

  function pbBuildCartSaveData(customerName, poNumber, cartId) {
    return {
      id: cartId || Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
      customerName: customerName,
      poNumber: poNumber,
      savedAt: new Date().toISOString(),
      itemCount: pbState.selections.reduce((sum, s) => sum + (s.selectedProducts?.length || 0), 0),
      selections: pbState.selections
        .filter(s => s.selectedProducts && s.selectedProducts.length > 0)
        .map(s => ({
          productClassId: s.productClassId,
          selectedProductIds: s.selectedProducts.map(p => p.id),
          productMeta: s.selectedProducts.map(p => ({
            id: p.id,
            selectedFinish: p.selectedFinish || null,
            selectedAccent: p.selectedAccent || null
          }))
        })),
      productSelections: JSON.parse(JSON.stringify(pbState.productSelections)),
      quantities: JSON.parse(JSON.stringify(pbState.quantities))
    };
  }

  function pbShowSaveModal() {
    const hasProducts = pbState.selections.some(s => s.selectedProducts && s.selectedProducts.length > 0);
    if (!hasProducts) {
      alert('Please add products to your package before saving a quote.');
      return;
    }

    // If we have an active cart, auto-update it without showing the modal
    if (pbState.activeCartId) {
      const carts = pbGetSavedCarts();
      const idx = carts.findIndex(c => c.id === pbState.activeCartId);
      if (idx !== -1) {
        const existing = carts[idx];
        carts[idx] = pbBuildCartSaveData(existing.customerName, existing.poNumber, existing.id);
        pbSaveSavedCarts(carts);
        pbUpdateSavedCartsUI();
        alert('Quote updated for ' + existing.customerName + (existing.poNumber ? ' (PO: ' + existing.poNumber + ')' : ''));
        return;
      }
    }

    const carts = pbGetSavedCarts();
    if (carts.length >= PB_MAX_CARTS) {
      alert('Maximum ' + PB_MAX_CARTS + ' saved quotes reached. Please delete an existing quote first.');
      return;
    }

    // Show modal for new cart
    const modal = document.getElementById('pbSaveCartModal');
    if (modal) {
      modal.style.display = 'flex';
      const nameInput = document.getElementById('pbSaveCartName');
      const poInput = document.getElementById('pbSaveCartPO');
      if (nameInput) {
        nameInput.value = '';
        nameInput.focus();
      }
      if (poInput) poInput.value = '';
    }
  }

  function pbCloseSaveModal() {
    const modal = document.getElementById('pbSaveCartModal');
    if (modal) modal.style.display = 'none';
  }

  function pbConfirmSaveCart() {
    const nameInput = document.getElementById('pbSaveCartName');
    const poInput = document.getElementById('pbSaveCartPO');
    const customerName = nameInput ? nameInput.value.trim() : '';
    const poNumber = poInput ? poInput.value.trim() : '';

    if (!customerName) {
      alert('Please enter a customer name.');
      return;
    }

    const cartData = pbBuildCartSaveData(customerName, poNumber);

    const carts = pbGetSavedCarts();
    carts.push(cartData);
    pbSaveSavedCarts(carts);

    pbCloseSaveModal();
    pbState.activeCartId = cartData.id;
    pbUpdateSavedCartsUI();
    alert('Quote saved for ' + customerName + (poNumber ? ' (PO: ' + poNumber + ')' : ''));
  }

  function pbRenderLoadPanelList(carts, filter) {
    const query = (filter || '').toLowerCase().trim();
    const filtered = query ? carts.filter(c =>
      c.customerName.toLowerCase().includes(query) ||
      (c.poNumber && c.poNumber.toLowerCase().includes(query))
    ) : carts;

    if (filtered.length === 0) {
      return '<div style="padding: 16px; text-align: center; color: #999;">' + (query ? 'No matching quotes' : 'No saved quotes') + '</div>';
    }

    return filtered.map(cart => {
      const date = new Date(cart.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const isActive = pbState.activeCartId === cart.id;
      return `
        <div class="pb-saved-cart-item ${isActive ? 'active' : ''}" data-cart-id="${cart.id}">
          <div class="pb-saved-cart-info" onclick="pbLoadCart('${cart.id}')">
            <div class="pb-saved-cart-name">${cart.customerName}${cart.poNumber ? ' <span style="color: #c9a55a;">(PO: ' + cart.poNumber + ')</span>' : ''}</div>
            <div class="pb-saved-cart-meta">${cart.itemCount} item${cart.itemCount !== 1 ? 's' : ''} &middot; ${date}</div>
          </div>
          <button class="pb-saved-cart-delete" onclick="event.stopPropagation(); pbDeleteCart('${cart.id}')" title="Delete">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      `;
    }).join('');
  }

  function pbShowLoadPanel() {
    const panel = document.getElementById('pbLoadCartPanel');
    if (!panel) return;

    const carts = pbGetSavedCarts();
    if (carts.length === 0) {
      panel.innerHTML = '<div style="padding: 20px; text-align: center; color: #999;">No saved quotes</div>';
    } else {
      panel.innerHTML =
        '<div class="pb-load-cart-search"><input type="text" id="pbLoadCartSearch" placeholder="Search by name or PO..." /></div>' +
        '<div id="pbLoadCartList">' + pbRenderLoadPanelList(carts, '') + '</div>';
    }

    panel.style.display = panel.style.display === 'block' ? 'none' : 'block';

    // Focus search input and attach filter listener
    const searchInput = document.getElementById('pbLoadCartSearch');
    if (searchInput) {
      searchInput.focus();
      searchInput.addEventListener('input', function() {
        const listEl = document.getElementById('pbLoadCartList');
        if (listEl) {
          listEl.innerHTML = pbRenderLoadPanelList(carts, this.value);
        }
      });
    }

    // Close panel when clicking outside
    setTimeout(() => {
      document.addEventListener('click', function closePanel(e) {
        if (!panel.contains(e.target) && !e.target.closest('.pb-load-cart-btn')) {
          panel.style.display = 'none';
          document.removeEventListener('click', closePanel);
        }
      });
    }, 0);
  }

  async function pbLoadCart(cartId) {
    const carts = pbGetSavedCarts();
    const cart = carts.find(c => c.id === cartId);
    if (!cart) {
      console.warn('pbLoadCart: cart not found for id', cartId);
      return;
    }

    // Close load panel
    const panel = document.getElementById('pbLoadCartPanel');
    if (panel) panel.style.display = 'none';

    window.PB_showLoading(true);

    try {
      // Clear current state
      pbState.productSelections = {};
      pbState.selections = [];
      pbState.quantities = {};
      pbState.expandedAccordion = null;

      // Restore filter selections
      pbState.productSelections = JSON.parse(JSON.stringify(cart.productSelections || {}));

      // Restore quantities
      pbState.quantities = JSON.parse(JSON.stringify(cart.quantities || {}));

      // For each saved selection, rebuild filtered products and find selected products
      for (const savedSelection of cart.selections) {
        const classId = savedSelection.productClassId;
        const options = pbState.filterOptions[classId];
        if (!options || !options.allProducts) {
          console.warn('pbLoadCart: skipping classId', classId, '- no filterOptions or allProducts');
          continue;
        }

        const hasFilterState = pbState.productSelections[classId] &&
          Object.values(pbState.productSelections[classId]).some(v => v !== null && v !== undefined);

        if (hasFilterState) {
          // Products were added via filters — rebuild through filter flow
          pbFetchFilteredProducts(classId);
        }

        // Find or create the selection entry
        let selectionIdx = pbState.selections.findIndex(s => s.productClassId === classId);
        if (selectionIdx < 0) {
          // No selection from filters (search-added products) — create one directly
          pbState.selections.push({
            productClassId: classId,
            productClassName: options.className,
            filteredProducts: [],
            selectedProducts: []
          });
          selectionIdx = pbState.selections.length - 1;
        }

        // Look up each saved product in allProducts (not just filtered, in case filters changed)
        const restoredProducts = [];
        for (const savedProductId of savedSelection.selectedProductIds) {
          const product = options.allProducts.find(p => p.id === savedProductId);
          if (product) {
            // Restore finish/accent stamps
            const meta = savedSelection.productMeta?.find(m => m.id === savedProductId);
            if (meta) {
              if (meta.selectedFinish) product.selectedFinish = meta.selectedFinish;
              if (meta.selectedAccent) product.selectedAccent = meta.selectedAccent;
            }
            restoredProducts.push(product);
          } else {
            console.warn('pbLoadCart: product not found in allProducts, id:', savedProductId);
          }
        }

        pbState.selections[selectionIdx].selectedProducts = restoredProducts;

        // Clean up quantities for products that weren't found
        savedSelection.selectedProductIds.forEach(id => {
          if (!restoredProducts.find(p => p.id === id)) {
            delete pbState.quantities[id];
          }
        });
      }

      pbState.activeCartId = cartId;

      // Show the builder UI (skip hero)
      pbStartBuilding();
      pbUpdateSavedCartsUI();
      window.PB_renderAccordions(pbState, window.PB_CONFIG);
      window.PB_updateCart(pbState, window.PB_CONFIG);
    } catch (error) {
      console.error('pbLoadCart: error loading cart', error);
      alert('Error loading cart. Please check the browser console for details.');
    } finally {
      window.PB_showLoading(false);
    }
  }

  function pbDeleteCart(cartId) {
    if (!confirm('Delete this saved quote?')) return;
    let carts = pbGetSavedCarts();
    carts = carts.filter(c => c.id !== cartId);
    pbSaveSavedCarts(carts);

    if (pbState.activeCartId === cartId) {
      pbState.activeCartId = null;
    }

    // Refresh the panel contents without toggling visibility
    const panel = document.getElementById('pbLoadCartPanel');
    if (panel && panel.style.display === 'block') {
      const updatedCarts = pbGetSavedCarts();
      if (updatedCarts.length === 0) {
        panel.innerHTML = '<div style="padding: 20px; text-align: center; color: #999;">No saved quotes</div>';
      } else {
        const listEl = document.getElementById('pbLoadCartList');
        const searchInput = document.getElementById('pbLoadCartSearch');
        const query = searchInput ? searchInput.value : '';
        if (listEl) {
          listEl.innerHTML = pbRenderLoadPanelList(updatedCarts, query);
        }
      }
    }
    pbUpdateSavedCartsUI();
  }

  function pbUpdateSavedCartsUI() {
    const badge = document.getElementById('pbSavedCartsBadge');
    const carts = pbGetSavedCarts();
    if (badge) {
      badge.textContent = carts.length;
      badge.style.display = carts.length > 0 ? 'inline-flex' : 'none';
    }

    // Update active cart label
    const label = document.getElementById('pbActiveCartLabel');
    if (label) {
      if (pbState.activeCartId) {
        const activeCart = carts.find(c => c.id === pbState.activeCartId);
        if (activeCart) {
          label.textContent = activeCart.customerName + (activeCart.poNumber ? ' (PO: ' + activeCart.poNumber + ')' : '');
          label.style.display = 'inline';
          return;
        }
      }
      label.textContent = '';
      label.style.display = 'none';
    }
  }

  function pbStartBuilding() {
    const heroSection = document.getElementById('pbHeroSection');
    const unifiedHeader = document.querySelector('.pb-unified-header');
    const leftSection = document.querySelector('.pb-left-section');

    if (heroSection) heroSection.classList.add('hidden');
    if (unifiedHeader) unifiedHeader.classList.add('visible');
    if (leftSection) {
      leftSection.classList.add('visible', 'full-width');
      leftSection.classList.remove('with-cart');
    }
  }

  function pbToggleCartVisibility() {
    const leftSection = document.querySelector('.pb-left-section');
    const rightSection = document.querySelector('.pb-right-section');

    if (rightSection && leftSection) {
      if (rightSection.classList.contains('visible')) {
        rightSection.classList.remove('visible');
        leftSection.classList.add('full-width');
        leftSection.classList.remove('with-cart');
      } else {
        rightSection.classList.add('visible');
        leftSection.classList.add('with-cart');
        leftSection.classList.remove('full-width');
      }
    }
  }

  function pbTogglePackageSummary() {
    const details = document.getElementById('pbSummaryDetails');
    const icon = document.getElementById('pbToggleIcon');

    details.classList.toggle('expanded');
    icon.classList.toggle('expanded');
  }

  function pbPrintPackage(printMode) {
    window.PB_printPackage(pbState, window.PB_CONFIG, printMode);
  }

  function pbSearchBySKU() {
    window.PB_searchBySKU(pbState);
  }

  function pbAddProductFromSearch(classId, productId) {
    window.PB_addProductFromSearch(classId, productId, pbState);
  }

  function pbRemoveProductFromSearch(classId, productId) {
    window.PB_removeProductFromSearch(classId, productId, pbState);
  }

  function pbChangeQuantity(classId, productId, delta) {
    const current = pbState.quantities[productId] || 1;
    const newQty = current + delta;

    if (newQty <= 0) {
      pbRemoveProduct(classId, productId);
      return;
    }

    pbState.quantities[productId] = newQty;
    window.PB_updateCart(pbState, window.PB_CONFIG);
  }


  function setupEventListeners() {
    const startBtn = document.getElementById('pbStartBtn');
    if (startBtn) {
      startBtn.addEventListener('click', pbStartBuilding);
    }

    // Search input - Enter key
    const searchInput = document.getElementById('pbSearchInput');
    if (searchInput) {
      searchInput.addEventListener('keypress', function(event) {
        if (event.key === 'Enter') {
          pbSearchBySKU();
        }
      });
    }

    // Search button
    const searchBtn = document.getElementById('pbSearchBtn');
    if (searchBtn) {
      searchBtn.addEventListener('click', pbSearchBySKU);
    }

    // Initialize SKU autocomplete
    if (window.PB_initSearchAutocomplete) {
      window.PB_initSearchAutocomplete(window.PB_STATE);
    }

    // Cart product buttons (event delegation for delete and quantity +/-)
    const cartContainer = document.getElementById('pbSummaryProducts');
    if (cartContainer) {
      cartContainer.addEventListener('click', function(event) {
        const deleteBtn = event.target.closest('.pb-product-delete');
        if (deleteBtn) {
          const classId = deleteBtn.dataset.classId;
          const productId = parseInt(deleteBtn.dataset.productId, 10);
          pbRemoveProduct(classId, productId);
          return;
        }

        const plusBtn = event.target.closest('.pb-qty-plus');
        if (plusBtn) {
          const classId = plusBtn.dataset.classId;
          const productId = parseInt(plusBtn.dataset.productId, 10);
          pbChangeQuantity(classId, productId, +1);
          return;
        }

        const minusBtn = event.target.closest('.pb-qty-minus');
        if (minusBtn) {
          const classId = minusBtn.dataset.classId;
          const productId = parseInt(minusBtn.dataset.productId, 10);
          pbChangeQuantity(classId, productId, -1);
          return;
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      pbInit();
      setupEventListeners();
    });
  } else {
    pbInit();
    setupEventListeners();
  }


  window.pbToggleAccordion = pbToggleAccordion;
  window.pbHandleSizeChange = pbHandleSizeChange;
  window.pbHandleFinishChange = pbHandleFinishChange;
  window.pbTogglePackageSummary = pbTogglePackageSummary;
  window.pbPrintPackage = pbPrintPackage;
  window.pbHandleFuelTypeChange = pbHandleFuelTypeChange;
  window.pbHandleAccentFinishChange = pbHandleAccentFinishChange;
  window.pbHandleHelixChange = pbHandleHelixChange;
  window.pbHandleHandleTypeChange = pbHandleHandleTypeChange;
  window.pbHandleMountTypeChange = pbHandleMountTypeChange;
  window.pbHandleRefrigeratorTypeChange = pbHandleRefrigeratorTypeChange;
  window.pbHandleColumnRefrigeratorTypeChange = pbHandleColumnRefrigeratorTypeChange;
  window.pbHandlePanelTypeChange = pbHandlePanelTypeChange;
  window.pbHandleMicrowaveTypeChange = pbHandleMicrowaveTypeChange;
  window.pbHandleRangetopTypeChange = pbHandleRangetopTypeChange;
  window.pbHandleUnderCounterTypeChange = pbHandleUnderCounterTypeChange;
  window.pbHandleNumberOfOvensChange = pbHandleNumberOfOvensChange;
  window.pbHandleInteriorFinishChange = pbHandleInteriorFinishChange;
  window.pbHandleDoorTypeChange = pbHandleDoorTypeChange;
  window.pbHandlePlumbingTypeChange = pbHandlePlumbingTypeChange;
  window.pbHandlePlumbingSubTypeChange = pbHandlePlumbingSubTypeChange;
  window.pbResetClass = pbResetClass;
  window.pbToggleProduct = pbToggleProduct;
  window.pbRestart = pbRestart;
  window.pbSearchBySKU = pbSearchBySKU;
  window.pbAddProductFromSearch = pbAddProductFromSearch;
  window.pbRemoveProductFromSearch = pbRemoveProductFromSearch;
  window.pbStartBuilding = pbStartBuilding;
  window.pbToggleCartVisibility = pbToggleCartVisibility;
  window.pbRemoveProduct = pbRemoveProduct;
  window.pbChangeQuantity = pbChangeQuantity;
  window.pbShowSaveModal = pbShowSaveModal;
  window.pbCloseSaveModal = pbCloseSaveModal;
  window.pbConfirmSaveCart = pbConfirmSaveCart;
  window.pbShowLoadPanel = pbShowLoadPanel;
  window.pbLoadCart = pbLoadCart;
  window.pbDeleteCart = pbDeleteCart;

  console.log('Package Builder Main loaded');
})();
